/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { datadogRum } from '@datadog/browser-rum';
import { getConfig } from '@utils/configs';

/**
 * Start DataDog RUM (Real user monitoring) service to record browser activity.
 * - The bill is based on the number of sessions which can be tracked by metric datadog.estimated_usage.rum.sessions
 * Ref. https://www.datadoghq.com/pricing/?product=real-user-monitoring--session-replay#products
 * Ref. https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-rum-usage/.)
 * - For deleting data due to leaking of PII, we need to delete data of all apps in a timeframe window and
 * we need to contact with Datadog support (https://docs.datadoghq.com/data_security/real_user_monitoring/#data-deletion)
 */
export function initDatadogRum() {
  const config = getConfig();
  if (!config.datadogRum.isEnabled) {
    return;
  }
  // Ref. https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration
  datadogRum.init({
    applicationId: config.datadogRum.applicationId,
    clientToken: config.datadogRum.clientToken,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    // We attach the env to service because the metric datadog.estimated_usage.rum.sessions does not have tag env.
    // By attaching the env to service name we can count and alert the usage on sandbox separatedly.
    service: `ron-client-${config.nodeEnv}`,
    env: config.nodeEnv,
    version: config.version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [window.location.origin],
    // defaultPrivacyLevel=mask: replace all text by XXXXX to avoid leaking PII.
    defaultPrivacyLevel: 'mask',
    // defaultPrivacyLevel: 'mask-user-input',
    // By default, enablePrivacyForActionName=false and DD try to capture the inner text where the user clicks on
    // the page and use it as the name of action. This very likely leaks PII information.
    // Thus, we turn it off and manually declare action names for DOM elements.
    enablePrivacyForActionName: true,
  });
}

/**
 * Set RUM username of current session.
 * This username can be any string and it can be used for searching in datadog UI.
 * Currently, we use the roomId and the view name (ex: "Room:<roomId>, View: notary_signing") as username
 * */
export function setRumSessionUserName(roomId: string, view: string, userId?: string) {
  const isNotary = view.indexOf('notary') > -1;
  datadogRum.setUser({
    ...datadogRum.getUser(),
    name: `room: ${roomId}, view: ${view}` + (!!userId ? `, user: ${userId}` : ''),
    roomId,
    view,
    userId,
    isNotary,
  });
}
